import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

// Types and Enums
interface SourceTypeTimeouts {
  deleted: number;
  dst: number;
  lost: number;
}

export enum SourceTypes {
  ais = 0,
  radar = 1,
  lrit = 2,
  aisSat = 3,
  adsB = 4,
  pps = 5,
  land = 6,
  aisVessel = 7,
  unspecified = 8,
  das = 9,
  droneSensor = 10,
}

// Available fields for favorite fields selection per target type
export const AVAILABLE_VESSEL_FIELDS = [
  'name',
  'imo',
  'mmsi',
  'callSign',
  'country',
  'aisType',
  'sourceType',
  'shipType',
  'width',
  'length',
  'draught',
  'heading',
  'rateOfTurn',
  'courseOverGround',
  'speedOverGround',
  'destination',
  'eta',
  'latitudeDegrees',
  'longitudeDegrees',
  'navigationalStatus',
] as const;

export const AVAILABLE_ATON_FIELDS = [
  'name',
  'mmsi',
  'country',
  'latitudeDegrees',
  'longitudeDegrees',
  'assignedMode',
  'atonType',
  'length',
  'width',
  'offPosition',
  'state',
  'status',
  'targetType',
] as const;

export const AVAILABLE_VIRTUAL_ATON_FIELDS = [
  'name',
  'mmsi',
  'country',
  'latitudeDegrees',
  'longitudeDegrees',
  'assignedMode',
  'virtualAtonType',
  'length',
  'width',
  'offPosition',
  'state',
  'status',
  'targetType',
] as const;

export const AVAILABLE_METHYD_FIELDS = [
  'stationName',
  'mmsi',
  'siteId',
  'latitudeDegrees',
  'longitudeDegrees',
  'airRelatedSettings',
  'waterRelatedSettings',
  'lastOrigin',
  'location',
  'message',
  'reportTimestamp',
  'dataTimeout',
  'owner',
] as const;

export const AVAILABLE_BASESTATION_FIELDS = [
  'name',
  'mmsi',
  'country',
  'latitudeDegrees',
  'longitudeDegrees',
  'vdlLoad',
] as const;

export const AVAILABLE_RADAR_FIELDS = [
  'id',
  'name',
  'speedOverGround',
  'courseOverGround',
  'lastRadarReport',
  'lastOrigin',
  'latitudeDegrees',
  'longitudeDegrees',
] as const;

export const AVAILABLE_DAS_FIELDS = [
  'trackId',
  'site',
  'priority',
  'lastReportTimestamp',
  'latitudeDegrees',
  'longitudeDegrees',
  'accuracy',
  'speed',
  'heading',
  'lastOrigin',
] as const;

export const AVAILABLE_DRONE_FIELDS = [
  'uasId',
  'macAddress',
  'uaType',
  'altitude',
  'speed',
  'heading',
  'latitudeDegrees',
  'longitudeDegrees',
  'operatorLatitude',
  'operatorLongitude',
  'lastReportTimestamp',
  'operationStatus',
] as const;

// Keep for backwards compatibility
export const AVAILABLE_SHIP_FIELDS = AVAILABLE_VESSEL_FIELDS;

export type VesselField = (typeof AVAILABLE_VESSEL_FIELDS)[number];
export type AtonField = (typeof AVAILABLE_ATON_FIELDS)[number];
export type VirtualAtonField = (typeof AVAILABLE_VIRTUAL_ATON_FIELDS)[number];
export type MethydField = (typeof AVAILABLE_METHYD_FIELDS)[number];
export type BaseStationField = (typeof AVAILABLE_BASESTATION_FIELDS)[number];
export type RadarField = (typeof AVAILABLE_RADAR_FIELDS)[number];
export type DasField = (typeof AVAILABLE_DAS_FIELDS)[number];
export type DroneField = (typeof AVAILABLE_DRONE_FIELDS)[number];
export type ShipField = VesselField;

// Target type keys for settings
export type TargetCardTargetType =
  | 'vessel'
  | 'aton'
  | 'virtualAton'
  | 'methyd'
  | 'baseStation'
  | 'radar'
  | 'das'
  | 'drone';

// Default favorite fields per target type
export const DEFAULT_FAVORITE_FIELDS: Record<TargetCardTargetType, string[]> = {
  vessel: [],
  aton: [],
  virtualAton: [],
  methyd: [],
  baseStation: [],
  radar: [],
  das: [],
  drone: [],
};

// Default header fields per target type
export const DEFAULT_HEADER_FIELDS: Record<TargetCardTargetType, string> = {
  vessel: 'name',
  aton: 'name',
  virtualAton: 'name',
  methyd: 'stationName',
  baseStation: 'name',
  radar: 'name',
  das: 'site',
  drone: 'uasId',
};

// Default subtitle fields per target type
export const DEFAULT_SUBTITLE_FIELDS: Record<TargetCardTargetType, string> = {
  vessel: 'callSign',
  aton: 'mmsi',
  virtualAton: 'mmsi',
  methyd: 'mmsi',
  baseStation: 'mmsi',
  radar: 'id',
  das: 'trackId',
  drone: 'macAddress',
};

// Schema for per-target-type favorite fields (max 4 per type)
const favoriteFieldsSchema = z.object({
  vessel: z.array(z.string()).max(4),
  aton: z.array(z.string()).max(4),
  virtualAton: z.array(z.string()).max(4),
  methyd: z.array(z.string()).max(4),
  baseStation: z.array(z.string()).max(4),
  radar: z.array(z.string()).max(4),
  das: z.array(z.string()).max(4),
  drone: z.array(z.string()).max(4),
});

// Schema for per-target-type header/subtitle fields
const perTypeFieldSchema = z.object({
  vessel: z.string(),
  aton: z.string(),
  virtualAton: z.string(),
  methyd: z.string(),
  baseStation: z.string(),
  radar: z.string(),
  das: z.string(),
  drone: z.string(),
});

// Combined schema for all target settings
const targetFormSchema = z.object({
  targetScale: z.number().min(75).max(325),
  predictorDuration: z.number().min(1).nullable(),
  sogVectorDuration: z.number().min(1).nullable(),
  targetCardExpandedByDefault: z.boolean(),
  favoriteFields: favoriteFieldsSchema,
  targetHeaderField: perTypeFieldSchema,
  targetSubtitleField: perTypeFieldSchema,
  targetTimeouts: z.array(
    z
      .object({
        deleted: z.number(),
        dst: z.number(),
        lost: z.number(),
      })
      .refine((data) => data.lost < data.deleted, {
        message: 'Lost timeout must not be larger than removed timeout',
        path: ['lost'],
      }),
  ),
});

export type TargetFormValues = z.infer<typeof targetFormSchema>;

export const useTargetForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const defaultTimeouts: SourceTypeTimeouts[] = useMemo(
    () => [
      { deleted: 14, dst: SourceTypes.ais, lost: 7 },
      { deleted: 14, dst: SourceTypes.radar, lost: 7 },
      { deleted: 14, dst: SourceTypes.lrit, lost: 7 },
      { deleted: 14, dst: SourceTypes.aisSat, lost: 7 },
      { deleted: 14, dst: SourceTypes.adsB, lost: 7 },
      { deleted: 14, dst: SourceTypes.pps, lost: 7 },
      { deleted: 14, dst: SourceTypes.land, lost: 7 },
      { deleted: 14, dst: SourceTypes.aisVessel, lost: 7 },
      { deleted: 14, dst: SourceTypes.unspecified, lost: 7 },
      { deleted: 14, dst: SourceTypes.das, lost: 7 },
      { deleted: 14, dst: SourceTypes.droneSensor, lost: 7 },
    ],
    [],
  );

  const currentTimeouts = useMemo(() => {
    const timeouts = settings['ribbonPlugin.settings']?.target_timeouts || [];

    // Create a map of existing timeouts by dst
    const existingTimeouts = new Map<number, SourceTypeTimeouts>(
      timeouts.map((timeout: SourceTypeTimeouts) => [timeout.dst, timeout]),
    );

    // Merge with defaults, keeping existing values where available
    // Filter out any invalid entries that might be missing dst property
    const mergedTimeouts = defaultTimeouts.map((defaultTimeout) => {
      const existing = existingTimeouts.get(defaultTimeout.dst);
      return existing ? existing : defaultTimeout;
    });

    return mergedTimeouts;
  }, [defaultTimeouts, settings]);

  // Helper function to migrate old format settings to new per-type format
  const migratedFavoriteFields = useMemo(() => {
    const stored = settings['ribbonPlugin.settings']?.favorite_fields;
    // If it's already an object with target types, use it
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
      return {
        ...DEFAULT_FAVORITE_FIELDS,
        ...stored,
      };
    }
    // If it's an array (old format), migrate vessel fields only
    if (Array.isArray(stored)) {
      return {
        ...DEFAULT_FAVORITE_FIELDS,
        vessel: stored,
      };
    }
    return DEFAULT_FAVORITE_FIELDS;
  }, [settings]);

  const migratedHeaderField = useMemo(() => {
    const stored = settings['ribbonPlugin.settings']?.target_header_field;
    // If it's already an object with target types, use it
    if (stored && typeof stored === 'object') {
      return {
        ...DEFAULT_HEADER_FIELDS,
        ...stored,
      };
    }
    // If it's a string (old format), use it for vessel only
    if (typeof stored === 'string') {
      return {
        ...DEFAULT_HEADER_FIELDS,
        vessel: stored,
      };
    }
    return DEFAULT_HEADER_FIELDS;
  }, [settings]);

  const migratedSubtitleField = useMemo(() => {
    const stored = settings['ribbonPlugin.settings']?.target_subtitle_field;
    // If it's already an object with target types, use it
    if (stored && typeof stored === 'object') {
      return {
        ...DEFAULT_SUBTITLE_FIELDS,
        ...stored,
      };
    }
    // If it's a string (old format), use it for vessel only
    if (typeof stored === 'string') {
      return {
        ...DEFAULT_SUBTITLE_FIELDS,
        vessel: stored,
      };
    }
    return DEFAULT_SUBTITLE_FIELDS;
  }, [settings]);

  const initialValues = useMemo(
    () => ({
      targetScale: settings['ribbonPlugin.settings']?.target_scale
        ? parseFloat(settings['ribbonPlugin.settings'].target_scale)
        : 100,
      predictorDuration: settings['ribbonPlugin.settings']?.predictor_time
        ? parseFloat(settings['ribbonPlugin.settings'].predictor_time)
        : null,
      sogVectorDuration: settings['ribbonPlugin.settings']?.sog_time
        ? parseFloat(settings['ribbonPlugin.settings'].sog_time)
        : null,
      targetCardExpandedByDefault:
        settings['ribbonPlugin.settings']?.target_card_expanded_by_default ??
        true,
      favoriteFields: migratedFavoriteFields,
      targetHeaderField: migratedHeaderField,
      targetSubtitleField: migratedSubtitleField,
      targetTimeouts: (currentTimeouts.slice() as SourceTypeTimeouts[]).sort(
        (a: SourceTypeTimeouts, b: SourceTypeTimeouts) => {
          if (a.dst === SourceTypes.ais) return -1;
          if (b.dst === SourceTypes.ais) return 1;
          if (a.dst === SourceTypes.aisSat) return -1;
          if (b.dst === SourceTypes.aisSat) return 1;
          if (a.dst === SourceTypes.aisVessel) return -1;
          if (b.dst === SourceTypes.aisVessel) return 1;
          if (a.dst === SourceTypes.das) return -1;
          if (b.dst === SourceTypes.das) return 1;
          return 0;
        },
      ),
    }),
    [currentTimeouts, migratedFavoriteFields, migratedHeaderField, migratedSubtitleField, settings],
  );

  const form = useForm<TargetFormValues>({
    resolver: zodResolver(targetFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: TargetFormValues) => {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        target_scale: data.targetScale.toString(),
        predictor_time: data.predictorDuration ? data.predictorDuration : 360,
        sog_time: data.sogVectorDuration ? data.sogVectorDuration : 900,
        scale_on: true,
        target_card_expanded_by_default: data.targetCardExpandedByDefault,
        favorite_fields: data.favoriteFields,
        target_header_field: data.targetHeaderField,
        target_subtitle_field: data.targetSubtitleField,
        target_timeouts: data.targetTimeouts,
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-target-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapTargetSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-target-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapTargetFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    SourceTypes,
  };
};
